import { useState } from 'react';
import { t } from '../i18n/index.js';
import { useSession } from '../lib/session.js';
import { AppShell, type NavSection } from '../patterns/AppShell.js';
import { PageBody } from '../patterns/PageHeader.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { IconBox, IconFilter, IconLock, IconStamp } from '../ui/Icon.js';
import { navigate, useRoute, type Route } from './routes.js';
import { TenantsPage } from '../features/tenants/TenantsPage.js';
import { TenantPage } from '../features/tenants/TenantPage.js';
import { AdminsPage } from '../features/admins/AdminsPage.js';
import { FlagsPage } from '../features/flags/FlagsPage.js';
import { AuditPage } from '../features/audit/AuditPage.js';

/**
 * The signed-in console: navigation, and whichever page the URL names.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE ROLE IS ON THE SCREEN, ALWAYS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Beside the email, not buried on a settings page. Three rungs can reach this
 * console and they can do very different things — `support` reads, `admin`
 * destroys customer data, `super_admin` changes who is an administrator — and
 * the commonest confusion is somebody looking for a control that is absent
 * because of who they are signed in as.
 *
 * Read from `/me`, which the platform re-reads from the session row on every
 * request, so a demotion shows here on the next call rather than whenever the
 * token happens to expire.
 */
export function Shell() {
  const { me, signOut } = useSession();
  const route = useRoute();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sections: NavSection[] = [
    {
      items: [
        { id: 'tenants', label: t('nav.tenants'), icon: IconBox },
        { id: 'admins', label: t('nav.admins'), icon: IconLock },
        { id: 'audit', label: t('nav.audit'), icon: IconStamp },
          { id: 'flags', label: t('nav.flags'), icon: IconFilter },
      ],
    },
  ];

  /*
    A tenant DETAIL page keeps `tenants` lit in the sidebar.

    Without this, drilling into an account leaves nothing highlighted, and the
    sidebar stops answering "where am I" at exactly the moment there is
    somewhere to come back from.
  */
  const active = route.name === 'tenant' ? 'tenants' : route.name;

  return (
    <AppShell
      sections={sections}
      active={active}
      onNavigate={(id) => {
        navigate({ name: id } as Route);
        setSidebarOpen(false);
      }}
      brand={
        <div className="flex items-center gap-2">
          <span className="font-semibold text-primary">{t('app.name')}</span>
          {/*
            Not decoration. This console can destroy any customer's books, and
            a person who has both consoles open needs to be able to tell in one
            glance which one they are typing into.
          */}
          <Badge intent="danger">{t('app.marker')}</Badge>
        </div>
      }
      account={
        me ? (
          <div className="flex flex-col gap-2">
            <div className="flex min-w-0 flex-col">
              {/* `truncate` needs `min-w-0` on every flex ancestor, or a long
                  address pushes the sign-out button off the rail instead of
                  clipping. `title` so the full value stays readable. */}
              <span className="truncate text-sm text-secondary" title={me.email}>
                {me.email}
              </span>
              <span className="text-xs text-tertiary">{me.role}</span>
            </div>
            <Button variant="secondary" size="sm" onClick={() => void signOut()}>
              {t('session.signOut')}
            </Button>
          </div>
        ) : null
      }
      sidebarOpen={sidebarOpen}
      onSidebarOpenChange={setSidebarOpen}
      /*
        The rail never collapses here. The product console offers it because a
        seller stares at wide financial tables all day and wants the width;
        this console has six accounts and a roster, and an icon rail whose
        three icons are Box, Lock and Stamp is a puzzle rather than a
        navigation. Passed explicitly rather than omitted so the decision is
        visible.
      */
      collapsed={false}
      onExpand={() => undefined}
    >
      {/*
        `PageBody` wraps EVERY page rather than each page bringing its own.

        It carries the page padding, the max width and the section gap. Put in
        each page instead, the first one written without it looks like a
        rendering bug — which is exactly what happened: this shell rendered the
        pages bare and the tables sat flush against the sidebar and the top of
        the window.

        The guard that flagged `PageBody` as unreachable was right that nothing
        imported it. The fix was to use it, not to delete it, and deleting it
        is what shipped the unpadded screen.
      */}
      <PageBody>
        <Page route={route} />
      </PageBody>
    </AppShell>
  );
}

/**
 * One switch, exhaustive over `Route`.
 *
 * No `default`, deliberately: adding a route to the union without adding it
 * here is a type error rather than a blank page.
 */
function Page({ route }: { route: Route }) {
  switch (route.name) {
    case 'tenant':
      return <TenantPage id={route.id} />;
    case 'admins':
      return <AdminsPage />;
    case 'audit':
      return <AuditPage />;
      case 'flags':
        return <FlagsPage />;
    case 'tenants':
      return <TenantsPage />;
  }
}
